import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useSettings } from "@/lib/settings";
import { usePageSeo } from "@/lib/usePageSeo";
import { Reveal } from "@/components/util/Reveal";
import { PageHero } from "@/components/layout/PageHero";
import { ArrowRight } from "lucide-react";
import { PUBLIC_DISCOVERY_GROUPS as DISCOVERY_GROUPS } from "@/lib/discoveryGroups";

/**
 * Portfolio index — masonry-style grid where each project preserves its
 * natural image aspect ratio so different works stand out visually at
 * the index level, not just once you click in. Cards link to the
 * dedicated /portfolio/[slug] case-study destinations added in Phase 8.1.
 *
 * Filter chips (Phase 8.1.2): derived from atomic tokens in each
 * project's compound category string (e.g. "Logo, Branding & 3D
 * Visualization" → ["Logo", "Branding", "3D Visualization"]). Clicking
 * a chip narrows the grid client-side to projects whose category
 * contains that token. "All" resets. Only rendered when there are
 * ≥2 distinct chips — otherwise a single-chip filter is noise.
 */
export default function PortfolioPage() {
  const [items, setItems] = useState([]);
  const s = useSettings();
  const [activeChip, setActiveChip] = useState("all");

  useEffect(() => {
    api.get("/portfolio").then((r) => setItems(r.data));
  }, []);

  const eyebrow = s.portfolio_eyebrow;
  const heading = s.portfolio_heading;
  const accent = s.portfolio_heading_accent ?? "";
  const description = s.portfolio_hero_description;

  // SEO Phase 3 — exact metadata proposed to owner for review.
  usePageSeo({
    title: "Portfolio — Ur Designs",
    description: "Real client work across branding, packaging, campaigns, event creative and 3D visualization by Ur Designs, based in Aruba and serving the Caribbean.",
    path: "/portfolio",
    image: items[0]?.image || undefined,
  });

  // Six shared discovery categories (same as Services); empty ones hidden.
  const chips = useMemo(
    () =>
      DISCOVERY_GROUPS.map((g) => ({
        ...g,
        count: items.filter((p) => (p.categories || []).includes(g.key)).length,
      })).filter((c) => c.count > 0),
    [items]
  );

  const filtered = useMemo(() => {
    if (activeChip === "all") return items;
    return items.filter((p) => (p.categories || []).includes(activeChip));
  }, [items, activeChip]);

  const showChips = chips.length >= 2;

  return (
    <div data-testid="portfolio-page" className="bg-[#0F0F0F]">
      <PageHero
        testId="portfolio-hero"
        eyebrow={eyebrow}
        heading={heading}
        accent={accent}
        accentInline={!!s.portfolio_accent_inline}
        showDot={!!s.portfolio_show_dot}
        description={description}
        ctaLabel={s.portfolio_hero_cta_label}
        ctaLink={s.portfolio_hero_cta_link}
      />
      {showChips && (
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-4">
          <div
            data-testid="portfolio-filter-chips"
            className="flex sm:flex-wrap sm:justify-center gap-2 pb-2 overflow-x-auto sm:overflow-visible -mx-4 px-4 sm:mx-0 sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <FilterChip
              label="All"
              active={activeChip === "all"}
              onClick={() => setActiveChip("all")}
              testId="portfolio-filter-all"
            />
            {chips.map((c) => (
              <FilterChip
                key={c.key}
                label={c.label}
                active={activeChip === c.key}
                onClick={() => setActiveChip(c.key)}
                testId={`portfolio-filter-${c.key}`}
              />
            ))}
          </div>
        </div>
      )}
      <section className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-4 sm:pt-6 pb-20 sm:pb-24">
        {filtered.length === 0 ? (
          <div data-testid="portfolio-empty" className="text-center py-24 text-[#A3A3A3] text-sm">
            No projects match that filter yet.
          </div>
        ) : (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-6 [column-fill:_balance]">
            {filtered.map((p, i) => (
              <Reveal key={p.id} delay={i * 50} className="mb-6 break-inside-avoid block">
                <Link
                  to={`/portfolio/${p.slug || p.id}`}
                  data-testid={`portfolio-${p.id}`}
                  className="group block rounded-2xl overflow-hidden border border-[#262626] bg-[#141414] hover:border-[#FF6600]/50 transition-colors"
                >
                  <div className="overflow-hidden">
                    <img
                      src={p.image}
                      alt={p.title}
                      className="w-full h-auto object-cover block group-hover:scale-[1.03] transition-transform duration-700"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5">
                    <div className="font-display text-xl font-bold text-white leading-tight">{p.title}</div>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <div className="text-xs text-[#A3A3A3] truncate">{p.category}</div>
                      <ArrowRight className="w-4 h-4 text-[#A3A3A3] group-hover:text-[#FF6600] group-hover:translate-x-0.5 transition-all shrink-0" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

const FilterChip = ({ label, active, onClick, testId }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`inline-flex shrink-0 whitespace-nowrap items-center rounded-full text-[11px] font-bold tracking-[0.15em] uppercase px-4 py-2 border transition-colors ${
      active
        ? "bg-[#FF6600] border-[#FF6600] text-white"
        : "border-[#333] text-white hover:border-[#FF6600] hover:text-[#FF6600]"
    }`}
  >
    {label}
  </button>
);
